import React, { useMemo } from "react";
import { Paper, Stack, Typography } from "@mui/material";
import { CalendarX } from "@phosphor-icons/react";

import { ModuleContainer } from "../styles";
import ModuleHeader from "../ModuleHeader";
import { generateDynamicLabels } from "../utils";
import CancellationsChart from "./CancellationsChart";
import EmptyCancellations from "./EmptyCancellations";
import PeriodToggleButton from "../PeriodToggleButton";
import ClientSelect from "../ClientSelect";
/** @typedef {import("api/typesDef").Client} Client */
/** @typedef {import("api/typesDef").ClinicianHours} ClinicianHours */

const LAST_INDEX = -1;

const proccessHour = (hour) => {
  const totalDuration = parseFloat(hour.total_duration);
  return totalDuration > 0 ? totalDuration : null;
};

/**
 * @component
 * @description `CancellationsNoShowsModule` component to display cancellations and no-shows hours by client and period (weeks or months) in a bar chart.
 * @param {Object} props
 * @param {ClinicianHours} props.dataHours - The data hours of cancellations and no-shows
 * @param {Client[]} props.clients - The list of clients assigned to the clinician
 * @param {string} props.period - The period (weeks or months)
 * @param {function} props.onPeriodChange - The function to change the period
 * @param {Client|"all"} props.selectedClient - The selected client
 * @param {function} props.onClientChange - The function to change the client
 * @param {boolean} props.isSmallScreen - Whether the screen is small
 */
const CancellationsNoShowsModule = ({
  dataHours,
  clients,
  period,
  onPeriodChange,
  selectedClient,
  onClientChange,
  isSmallScreen,
}) => {
  const isEmpty = dataHours?.scheduled?.length === 0;
  const datesLabels = generateDynamicLabels(dataHours?.scheduled, period, isSmallScreen);

  const mappedHours = useMemo(() => {
    return {
      scheduledHours: dataHours?.scheduled?.map(proccessHour) || [],
      yourCancellations: dataHours?.cancellations?.clinician?.map(proccessHour) || [],
      clientCancellations: dataHours?.cancellations?.client?.map(proccessHour) || [],
      noShows: dataHours?.noShows?.map(proccessHour) || [],
      other: dataHours?.cancellations?.other?.map(proccessHour) || [],
      dates: dataHours?.scheduled?.map((hour) => hour.date) || [],
    };
  }, [dataHours]);

  const lastPeriodScheduled = mappedHours?.scheduledHours.at(LAST_INDEX);
  const lastPeriodOther = mappedHours?.other.at(LAST_INDEX);
  const lastPeriodNoShow = mappedHours?.noShows.at(LAST_INDEX);
  const lastPeriodBCBACancellations = mappedHours?.yourCancellations.at(LAST_INDEX);
  const lastPeriodClientCancellations = mappedHours?.clientCancellations.at(LAST_INDEX);

  // Calculating the missed session rate across both BCBA and client cancellations and no-shows this week or month
  const lastPeriodCancellations =
    lastPeriodBCBACancellations +
    lastPeriodClientCancellations +
    lastPeriodNoShow +
    lastPeriodOther;

  const missedSessionsPercentage = (lastPeriodCancellations / lastPeriodScheduled) * 100;

  const missedSessions = Number.isNaN(missedSessionsPercentage)
    ? 0
    : missedSessionsPercentage.toFixed(2);

  const textForPerson = selectedClient === "all" ? "Current" : `${selectedClient.name}'s`;

  return (
    <Paper component="section" elevation={2} sx={ModuleContainer}>
      <Stack gap={7}>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          justifyContent="space-between"
          alignItems="baseline"
          gap={2}
        >
          <ModuleHeader
            Icon={CalendarX}
            title="Cancellations & No-Shows"
            subtitle={
              isEmpty ? null : (
                <>
                  {textForPerson} missed session rate this week is{" "}
                  <Typography component="span" color="primary.main" variant="subtitle2">
                    {missedSessions}%
                  </Typography>
                </>
              )
            }
          />

          <Stack direction="row" gap={3} sx={{ width: isSmallScreen ? "100%" : "auto" }}>
            {!isSmallScreen && (
              <ClientSelect
                clients={clients}
                selectedClient={selectedClient}
                onClientChange={onClientChange}
                data-testid="client-select-top"
              />
            )}
            <PeriodToggleButton
              period={period}
              onPeriodChange={onPeriodChange}
              isSmallScreen={isSmallScreen}
            />
          </Stack>
        </Stack>

        {isEmpty ? (
          <EmptyCancellations />
        ) : (
          <Stack sx={{ minHeight: "200px", maxHeight: isSmallScreen ? "200px" : "300px" }}>
            <CancellationsChart
              hoursData={mappedHours}
              period={period}
              isSmallScreen={isSmallScreen}
              labels={datesLabels}
            />
          </Stack>
        )}

        {isSmallScreen && !isEmpty && (
          <ClientSelect
            clients={clients}
            onClientChange={onClientChange}
            selectedClient={selectedClient}
            fullWidth
            data-testid="client-select-bottom"
          />
        )}
      </Stack>
    </Paper>
  );
};

export default CancellationsNoShowsModule;
